import React from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { AgreementType } from 'api/types';
import {
  perSessionAchCompletionTimeKey,
  perSessionAutopayBannerKey,
} from 'constants/localStorageKeys';
import { AchModalEntryPoint, trackAchModalOpened } from 'modules/status/utils/trackAchModalEvent';
import { useTranslation } from 'react-i18next';
import { Banner } from 'shared/components/Banner/Banner';
import { EarlyAccessModalType } from '../EarlyAccessModals/EarlyAccessModals';

// Since we don't know how long it takes Docusign to send webhook to us to update agreement ach_elected,
// we have 1 hour window threshold in local storage in case ach_elected still return false
const ACH_ELECTED_EXPIRED_WINDOW_CHECK = 60 * 60 * 1000;

interface AchEnrollmentBannerProps {
  achFormCompletionTime: number | null;
  achModalEnabled: boolean;
  onOpenModal: (modal: EarlyAccessModalType) => void;
  selectedAgreement?: AgreementType;
  isEarlyAccess: boolean;
  isAchRedirect: boolean;
}

export function AchEnrollmentBanner({
  achFormCompletionTime,
  achModalEnabled,
  onOpenModal,
  selectedAgreement,
  isEarlyAccess,
  isAchRedirect,
}: AchEnrollmentBannerProps) {
  const { t } = useTranslation();

  if (!achModalEnabled) return null;

  if (selectedAgreement?.ach_elected === null) return null;

  if (
    !selectedAgreement ||
    (selectedAgreement?.ach_elected && !isAchRedirect && selectedAgreement.ach_verified !== false)
  ) {
    return null;
  }

  if (!isEarlyAccess) return null;

  if (achFormCompletionTime !== null) {
    if (achFormCompletionTime + ACH_ELECTED_EXPIRED_WINDOW_CHECK > Date.now()) {
      return null;
    }
  } else {
    const perSessionAchCompletionTimeRaw = localStorage.getItem(perSessionAchCompletionTimeKey);
    if (perSessionAchCompletionTimeRaw !== null) {
      const achCompletionTimeObj = JSON.parse(perSessionAchCompletionTimeRaw);
      if (achCompletionTimeObj && achCompletionTimeObj[selectedAgreement.id]) {
        if (
          achCompletionTimeObj[selectedAgreement.id] + ACH_ELECTED_EXPIRED_WINDOW_CHECK >
          Date.now()
        ) {
          return null;
        }
      }
    }
  }

  return (
    <Banner
      bannerKey={perSessionAutopayBannerKey(selectedAgreement?.id || '')}
      bannerText={
        selectedAgreement.ach_verified === false
          ? t('autopayBanner.signUpIssue')
          : t('autopayBanner.goodbyeToChecks')
      }
      link={{
        text:
          selectedAgreement.ach_verified === false
            ? t('autopayBanner.tapToReEnter')
            : t('autopayBanner.clickHere'),
        onClick: () => {
          onOpenModal(EarlyAccessModalType.AUTOPAY);
          trackAchModalOpened(AchModalEntryPoint.BANNER);
          datadogRum.addAction('ach-enrollment-banner-clicked');
        },
      }}
    />
  );
}

export default AchEnrollmentBanner;
